import z from 'zod'

import { PersonDisplay } from '@/models/features/display-column.models'
import { packagesQueryFiltersSchema } from '@/pages/packages/packages-list/packages-list.constants'
import { Building } from '@/reducers/types'

export interface ResidentData {
  residentUnit?: string
  apartment?: string
  buildingNumber?: string
  residentBuildingNumber?: string
}

export const PackageStatusSchema = z.enum(['Pending', 'Requested', 'Received', 'Archived'])
export type PackageStatus = z.infer<typeof PackageStatusSchema>

export const PackageCarrierSchema = z.enum(['fedex', 'ups', 'usps', 'dhl', 'amazon', 'other'])
export type PackageCarrier = z.infer<typeof PackageCarrierSchema>

export type DeliveryType = 'toHand' | 'dropByDoor' | null

export interface PackageClient {
  id: number
  inHouse: boolean
}

export interface PackageModel {
  // Note: the BE's per-column person details - only the fields enabled in its settings (EL-15064)
  residentDisplay?: PersonDisplay
  checkedInDisplay?: PersonDisplay
  checkedOutDisplay?: PersonDisplay
  id: number
  // Note: checkinBy / checkoutBy are the staff member's email
  checkinBy?: string | null
  checkinByName: string | null
  checkinByFirstName?: string | null
  checkinByLastName?: string | null
  checkinByAvatar?: string | null
  checkinByRole?: string | null
  checkinAt: number | null
  checkoutBy: string | null
  checkoutByName: string | null
  checkoutByFirstName?: string | null
  checkoutByLastName?: string | null
  checkoutByAvatar?: string | null
  checkoutByRole?: string | null
  checkoutAt: number | null
  residentName: string
  residentFirstName?: string | null
  residentLastName?: string | null
  residentStreet?: string | null
  residentUnit: string
  residentBuildingNumber: string
  inHouseDelivery: boolean
  residentPhone: number | null
  residentEmail: string
  residentAvatar: string
  vacationStartDate: number | null
  vacationEndDate: number | null
  vacationNote: string | null
  isResidentOnVacation: boolean
  status: PackageStatus
  carrier: PackageCarrier
  comment: string
  perishable: boolean
  trackingNumber: string
  image: string | File | Blob
  signature: string | null
  location: string
  accessCode: string
  deliveryType: DeliveryType
  deliveryImage: string | null
  deliveryDate: number | null
  deliveryHours: number | null
  deliveryHoursEnd: number | null
  failedDeliveryDate: number | null
  requestedDate: number | null
  requestedFirst: number
  pendingFirst: number
  expiredAt: number | null
  buildingId: number
  packageRoomSessionId: number | null
  clientId: number
  client: PackageClient
  updatedAt: number | null
}

export interface PackageCarrierConfig {
  code: number
  name: string
}

export interface PackageRoomConfig {
  id: number
  name: string
  url: string
}

export const BusinessHourSchema = z.object({
  endTime: z.number(),
  name: z.string(),
  startTime: z.number(),
})

export type BusinessHour = z.infer<typeof BusinessHourSchema>

export const PackageLocationSchema = z.object({
  id: z.number().optional(),
  name: z.string(),
  packageRoom: z.boolean().optional(),
})

export type PackageLocation = z.infer<typeof PackageLocationSchema>

export interface PackageSettings {
  locations: PackageLocation[]
  createdAt: number
  updatedAt: number
  id: number
  d2dMode: boolean
  deliverySlotLength: number
  businessHours: BusinessHour[]
  maxDeliveriesPerSlot: number
  allowASAP: boolean
  allowQuickScan: boolean
  trackingNumberRequired: boolean
  locationRequired: boolean
  photoRequired: boolean
  carrierAccessCodeEnabled: boolean
  maskUsernameEnabled: boolean
  buildingNumberDisplayEnabled: boolean
  buildingId: number
  packageRooms: PackageRoomConfig[]
  carriers: PackageCarrierConfig[]
}

export interface GetPackageColumnsParams {
  timezone: string
  selectedBuilding: Building | null
}

// Note: shared across modules (Packages, Checklists/Inspections, Gift Cards, ...) for embedding
// a module's table inside another page (e.g. the Resident Profile) scoped to - and with a locked
// resident field for - a single, already-known resident. `email`/`phone` are optional since only
// Gift Cards' "Send gift" CTA needs them (as the locked recipient's contact info).
export type LockedResident = {
  id: number
  name: string
  apartment?: number | null
  buildingNumber?: string | null
  email?: string
  phone?: string | number | null
}

export interface LocationOption {
  value: string
  label: string
}

export type PackagesRequest = Partial<z.infer<typeof packagesQueryFiltersSchema>>

export interface PackageRoomSessionRoom {
  id: number
  name: string
}

export interface PackageRoomSession {
  sessionId: number
  startTime: number
  endTime: number
  packageCount: number
  carriers: string[]
  packageRoom: PackageRoomSessionRoom
}

export interface PackageLogResponse {
  data: PackageRoomSession[]
  total: number
}

export interface PackageRoomSessionDetailsResponse {
  endTime: number
  packages: PackageModel[]
}
